/**
 * PKCE (Proof Key for Code Exchange) Utilities
 *
 * Thin wrapper: delegates crypto operations to @levelup/central-auth shared helpers.
 * Keeps session storage management local (the shared package uses localStorage
 * via initiateCentralLogin, but the callback handler here uses sessionStorage
 * for short-lived PKCE data).
 *
 * IMPORTANT: Tokens are stored in memory only, NOT localStorage (XSS risk).
 * Session storage is used only for short-lived PKCE verifier during auth flow.
 */

import {
  PKCE_VERIFIER_KEY,
  OAUTH_STATE_KEY,
  OAUTH_RETURN_PATH_KEY,
} from '@levelup/central-auth';

// Re-export shared storage key constants
export { PKCE_VERIFIER_KEY, OAUTH_STATE_KEY, OAUTH_RETURN_PATH_KEY };

// ============================================================
// Session storage helpers (app-local — short-lived PKCE data)
// ============================================================

/**
 * Store PKCE verifier in session storage.
 */
export function storeVerifier(verifier: string): void {
  sessionStorage.setItem(PKCE_VERIFIER_KEY, verifier);
}

/**
 * Retrieve PKCE verifier from session storage.
 */
export function retrieveVerifier(): string | null {
  return sessionStorage.getItem(PKCE_VERIFIER_KEY);
}

/**
 * Clear PKCE verifier from session storage.
 */
export function clearVerifier(): void {
  sessionStorage.removeItem(PKCE_VERIFIER_KEY);
}

/**
 * Store OAuth state in session storage.
 */
export function storeState(state: string): void {
  sessionStorage.setItem(OAUTH_STATE_KEY, state);
}

/**
 * Retrieve OAuth state from session storage.
 */
export function retrieveState(): string | null {
  return sessionStorage.getItem(OAUTH_STATE_KEY);
}

/**
 * Clear OAuth state from session storage.
 */
export function clearState(): void {
  sessionStorage.removeItem(OAUTH_STATE_KEY);
}

/**
 * Store the URL the user intended to visit before being redirected to Central.
 */
export function storeReturnUrl(url: string): void {
  sessionStorage.setItem(OAUTH_RETURN_PATH_KEY, url);
}

/**
 * Retrieve and clear the stored return URL.
 */
export function retrieveReturnUrl(): string | null {
  const url = sessionStorage.getItem(OAUTH_RETURN_PATH_KEY);
  sessionStorage.removeItem(OAUTH_RETURN_PATH_KEY);
  return url;
}

/**
 * Clear all OIDC session data.
 */
export function clearOidcSession(): void {
  clearVerifier();
  clearState();
  sessionStorage.removeItem(OAUTH_RETURN_PATH_KEY);
}
